import React from 'react';
import styled from '@emotion/styled';
import { History, Edit, AlertCircle } from 'lucide-react';
import { colors } from '../../theme/colors';

const ContentSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContentTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
`;

const FieldHistorySection = ({ historyData, renderStatusBar }) => {
  if (historyData.length === 0) {
    return (
      <>
        {renderStatusBar && renderStatusBar()}
        <ContentSection>
          <ContentTitle>Field History</ContentTitle>
          <div 
            data-cy="no-history-message"
            style={{ 
              background: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: '6px', 
              padding: '2rem',
              textAlign: 'center',
              color: '#6b7280'
            }}>
            <History size={32} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <div style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem' }}>
              No History Available
            </div>
            <div style={{ fontSize: '0.875rem' }}>
              This field has no recorded changes or comments yet.
            </div>
          </div>
        </ContentSection>
      </>
    );
  }

  return (
    <>
      {renderStatusBar && renderStatusBar()}
      <ContentSection>
        <ContentTitle>Field History</ContentTitle>
        <div data-cy="history-entries" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {historyData.map((entry, index) => (
            <div 
              key={index} 
              data-cy="history-entry"
              style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '1rem',
              position: 'relative'
            }}>
              {/* Timeline line */}
              {index < historyData.length - 1 && (
                <div style={{
                  position: 'absolute',
                  left: '1.5rem',
                  top: '3rem',
                  bottom: '-1rem',
                  width: '2px',
                  background: '#e5e7eb'
                }} />
              )}
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                {/* Icon */}
                <div style={{
                  width: '2rem',
                  height: '2rem',
                  borderRadius: '50%',
                  background: entry.type === 'value_change' ? colors.primary : 
                             entry.type === 'comment' ? '#10b981' : '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  zIndex: 1,
                  position: 'relative'
                }}>
                  {entry.type === 'value_change' ? (
                    <Edit size={14} style={{ color: 'white' }} />
                  ) : entry.type === 'comment' ? (
                    <History size={14} style={{ color: 'white' }} />
                  ) : (
                    <AlertCircle size={14} style={{ color: 'white' }} />
                  )}
                </div>
                
                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                        {entry.title}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {entry.timestamp} • {entry.user}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: entry.type === 'value_change' ? '#3b82f6' : 
                             entry.type === 'comment' ? '#10b981' : '#f59e0b',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {entry.type.replace('_', ' ')}
                    </div>
                  </div>
                  
                  {entry.description && (
                    <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                      {entry.description}
                    </div>
                  )}
                  
                  {entry.changes && (
                    <div style={{ 
                      background: '#f8fafc', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '6px', 
                      padding: '0.75rem',
                      marginBottom: '0.75rem'
                    }}>
                      {Object.entries(entry.changes).map(([field, change]) => (
                        <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#6b7280', minWidth: '80px' }}>
                            {field.replace('_', ' ')}:
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#ef4444', textDecoration: 'line-through' }}>
                            {change.from}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>→</span>
                          <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '500' }}>
                            {change.to}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {entry.comment && (
                    <div style={{ 
                      background: '#f0f9ff', 
                      border: '1px solid #0ea5e9', 
                      borderRadius: '6px', 
                      padding: '0.75rem'
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: '500', color: '#0c4a6e', marginBottom: '0.25rem' }}>
                        Comment:
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#0c4a6e', lineHeight: '1.5' }}>
                        {entry.comment}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ContentSection>
    </>
  );
};

export default FieldHistorySection;
